import React, { useState, useEffect, useMemo } from 'react';
import Dialog from "@material-ui/core/Dialog";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogTitle from "../../../../Common/UI/Dialog/DialogTitle";
import Spinner from "../../../../Common/UI/Spinner/Spinner";
import { useNotification } from "../../../../../core/contexts/NotificationContext";
import * as UserApi from '../../../../Common/Apis/UserApi';

const RolePermissionEdit = (props) => {
  const { isShow, item, onClose, onRefresh } = props;
  const { showSuccess } = useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [permissionList, setPermissionList] = useState();
  const [rolePermissions, setRolePermissions] = useState([]);

  useEffect(() => {
    const getRolePermissions = async () => {
      const initRolePermission = [];

      try {
        setIsLoading(true);

        const permissions = await UserApi.getUserRolePermission(item.RoleId);

        // Only consider available permissions.
        const availablePermissions = permissions?.filter((p) => p?.PermissionAvailable === "True");

        availablePermissions?.forEach((p) => {
          if (p?.HasPermission === "True") {
            initRolePermission.push(p?.PermissionId);
          }
        });

        // Group available permissions by screen, then by module.
        const screenGroup = availablePermissions?.reduce((r, p) => {
          r[p.ScreenName] = [...(r[p.ScreenName] || []), p];
          return r;
        }, {});

        const moduleGroup = Object.entries(screenGroup).reduce(function (r, entry) {
          const [key, value] = entry;
          const itemArray = { key: key, items: value };
          r[value[0].ModuleName] = [...(r[value[0].ModuleName] || []), itemArray];
          return r;
        }, {});

        setRolePermissions(initRolePermission);
        setPermissionList(moduleGroup);
      } catch (err) {
        console.log("err", err);
      }

      setIsLoading(false);
    };

    if (item) {
      getRolePermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const updatePermissionRole = async () => {
    try {
      setIsLoading(true);

      await UserApi.updateUserRolePermission(item.RoleId, {
        permissionIds: rolePermissions,
      });

      showSuccess({ content: "Successfully saved", timeout: 3000 });
      setPermissionList();
      setRolePermissions();
      setIsLoading(false);
      onRefresh();
      onClose();
    } catch (err) {
      console.log("err", err);
    }
    setIsLoading(false);
  };

  const getPermissionModule = (moduleItems) => {
    return (
      <>
        {moduleItems?.map((moduleItem) => {
          return (
            <div className="row border-bottom" key={moduleItem.key}>
              <div className="ml-1" style={{ minWidth: "180px" }}>
                {moduleItem.key}
              </div>
              <div className=" d-flex ">
                {getPermissionScreen(moduleItem.items)}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const getPermissionScreen = (screenItems) => {
    const viewPermission = [];
    const editPermission = [];
    const deletePermission = [];
    const addPermission = [];

    let hasViewPermission = false;
    let hasEditPermission = false;
    let hasDeletePermission = false;
    let hasAddPermission = false;

    screenItems.forEach((scr) => {
      const permission = scr.PermissionName.toLowerCase();

      if (permission === "view") {
        viewPermission.push(scr.PermissionId);
        if (scr.HasPermission === "True") hasViewPermission = true;
      } else if (permission === "delete") {
        deletePermission.push(scr.PermissionId);
        if (scr.HasPermission === "True") hasDeletePermission = true;
      } else if (permission === "add") {
        addPermission.push(scr.PermissionId);
        if (scr.HasPermission === "True") hasAddPermission = true;
      } else {
        editPermission.push(scr.PermissionId);
        if (scr.HasPermission === "True") hasEditPermission = true;
      }
    });

    const permissions = [];

    if (viewPermission.length > 0) {
      permissions.push({ id: viewPermission, label: "View", checked: hasViewPermission, available: true });
    }
    if (editPermission.length > 0) {
      permissions.push({ id: editPermission, label: "Edit", checked: hasEditPermission, available: true });
    }
    if (addPermission.length > 0) {
      permissions.push({ id: addPermission, label: "Add", checked: hasAddPermission, available: true });
    }
    if (deletePermission.length > 0) {
      permissions.push({ id: deletePermission, label: "Delete", checked: hasDeletePermission, available: true });
    }

    // Split permissions into rows of 2.
    const rows = [];
    for (let i = 0; i < permissions.length; i += 2) {
      rows.push(permissions.slice(i, i + 2));
    }

    return (
      <div className="d-flex flex-column">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="d-flex mb-2">
            {row.map((permission) => (
              <span
                key={permission.label}
                className="m-2"
                style={{ minWidth: "100px" }}
              >
                <input
                  className="mr-2"
                  type="checkbox"
                  onChange={permissionChange}
                  id={`chk_permission_${permission.label.toLowerCase()}_${permission.id}`}
                  value={permission.id}
                  defaultChecked={permission.checked}
                  disabled={!permission.available}
                />
                <label
                  htmlFor={`chk_permission_${permission.label.toLowerCase()}_${permission.id}`}
                  style={{ color: !permission.available ? "#999" : "inherit" }}
                >
                  {permission.label}
                </label>
              </span>
            ))}
            {row.length === 1 && (
              <span className="m-2" style={{ minWidth: "100px", visibility: "hidden" }} />
            )}
          </div>
        ))}
      </div>
    );
  };

  const getPermissionList = useMemo(
    () => {
      if (!permissionList) {
        return;
      }
      return Object.entries(permissionList)?.map((entry) => {
        const [key, value] = entry;
        const keyId = key.replace(" ", "-");
        return (
          <div className="" key={keyId}>
            <div
              className="border-bottom cursor-pointer"
              id={`heading${keyId}`}
              data-toggle="collapse"
              data-target={`#${keyId}`}
              aria-expanded="true"
              aria-controls={keyId}
            >
              <div className="m-3 ">
                <h6>{key}</h6>
              </div>
            </div>

            <div
              id={keyId}
              className="collapse"
              aria-labelledby={`heading${keyId}`}
              data-parent="#accordion"
            >
              <div className="card-body">{getPermissionModule(value)}</div>
            </div>
          </div>
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item, permissionList]
  );

  function permissionChange(e) {
    let permissionIds = e.target.value.split(",");
    permissionIds = permissionIds.map((x) => parseInt(x));

    if (e.target.checked) {
      setRolePermissions((p) => [...(p || []), ...permissionIds]);
    } else {
      permissionIds.forEach((permission) => {
        setRolePermissions((p) => p.filter((id) => id !== permission));
      });
    }
  }

  return (
    <>
      <Dialog
        onClose={onClose}
        aria-labelledby="customized-dialog"
        open={isShow}
        PaperProps={{ style: { borderRadius: "1rem", margin: 1, minWidth: "380px" } }}
      >
        <DialogTitle id="customized-dialog-title" onClose={onClose}>
          Role Permissions
        </DialogTitle>
        <MuiDialogContent dividers>
          <form onSubmit={() => updatePermissionRole()}>
            <div className="modal-content model-dialog">
              <div className="modal-body  pt-0 pb-0">
                <div className="row mt-3 mb-5">
                  <div className="col-12">
                    <div className="p-number">
                      <div id="accordion">
                        {getPermissionList}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </MuiDialogContent>
        <MuiDialogActions style={{ margin: 0, padding: 8 }}>
          <Button
            className="mb-3"
            style={{ fontWeight: "600" }}
            variant="contained"
            onClick={() => updatePermissionRole()}
            color="primary"
          >
            Save
          </Button>
        </MuiDialogActions>
      </Dialog>
      {isLoading && <Spinner />}
    </>
  );
};

export default RolePermissionEdit;
